import { useEffect, useState } from "react";
import { Sun, Moon, Settings as SettingsIcon, Menu, RotateCcw } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { RefreshCw, Bike, LogOut, ClipboardList } from "lucide-react";

const ThemeToggle = () => {
  const [light, setLight] = useState(() => localStorage.getItem("theme") !== "dark");
  useEffect(() => {
    document.documentElement.classList.toggle("light", light);
    localStorage.setItem("theme", light ? "light" : "dark");
  }, [light]);
  return (
    <Button data-testid="theme-toggle-btn" size="sm" variant="ghost" onClick={() => setLight(!light)}
      className="text-slate-400 hover:text-white hover:bg-slate-800 h-8 w-8 p-0 sm:h-9 sm:w-9" title="Toggle theme">
      {light ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />}
    </Button>
  );
};

export const TopBar = ({ data, refreshing, onRefresh, tab, setTab, navItems }) => {
  const { user, logout } = useAuth();
  const [company, setCompany] = useState("OPM Bajaj");
  const [menuOpen, setMenuOpen] = useState(false);
  useEffect(() => { api.get("/settings/company").then((r) => setCompany(r.data.name)).catch(() => {}); }, []);
  const go = (v) => { setTab(v); setMenuOpen(false); };
  return (
    <header className="sticky top-0 z-50 backdrop-blur-md bg-slate-900/85 border-b border-slate-800">
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 h-14 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 sm:gap-3 min-w-0">
          <Button data-testid="menu-btn" size="sm" variant="ghost" onClick={() => setMenuOpen(true)}
            className="h-9 w-9 p-0 text-slate-300 hover:text-white hover:bg-slate-800 border border-slate-700 rounded-lg shrink-0" title="Menu">
            <Menu className="h-5 w-5" />
          </Button>
          <div className="h-8 w-8 sm:h-9 sm:w-9 rounded-md bg-blue-600 flex items-center justify-center shrink-0">
            <Bike className="h-5 w-5 text-white" />
          </div>
          <h1 className="text-base sm:text-lg font-extrabold tracking-tight leading-none truncate" style={{ fontFamily: "Outfit, sans-serif" }}>{company}</h1>
        </div>
        <div className="flex items-center gap-1 sm:gap-2">
          <Badge data-testid="sync-status-badge" variant="outline"
            className={`hidden md:inline-flex ${!data ? "border-slate-500/50 text-slate-400" : data?.source === "google_sheet" ? "border-emerald-500/50 text-emerald-400" : "border-amber-500/50 text-amber-400"}`}>
            {!data ? "Syncing…" : data?.source === "google_sheet" ? "Live" : "Sample"}
          </Badge>
          <ThemeToggle />
          <Button data-testid="topbar-reset-btn" size="sm" variant="ghost" onClick={() => window.dispatchEvent(new CustomEvent("reset-filters"))}
            className="text-slate-400 hover:text-white hover:bg-slate-800 h-8 w-8 p-0 sm:h-9 sm:w-9" title="Reset filters (this year)">
            <RotateCcw className="h-4 w-4" />
          </Button>
          <Button data-testid="refresh-sheet-btn" size="sm" onClick={onRefresh} disabled={refreshing}
            className="bg-blue-600 hover:bg-blue-500 text-white active:scale-95 transition-colors h-8 sm:h-9 px-2.5 sm:px-3" title="Refresh sheet data">
            <RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
          </Button>
          <div className="flex items-center gap-0.5 sm:gap-1.5 pl-1 sm:pl-2 border-l border-slate-700">
            <Button data-testid="logout-btn" size="sm" variant="ghost" onClick={logout} title="Sign out"
              className="text-slate-400 hover:text-white hover:bg-slate-800 h-8 w-8 sm:h-9 sm:w-9 p-0">
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
      <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
        <SheetContent side="left" data-testid="menu-drawer" className="w-64 bg-[#0B0F17] border-[#334155] text-slate-100 p-0">
          <SheetHeader className="px-4 py-4 border-b border-slate-800">
            <SheetTitle className="text-slate-100 flex items-center gap-2 text-base" style={{ fontFamily: "Outfit, sans-serif" }}>
              <div className="h-8 w-8 rounded-md bg-blue-600 flex items-center justify-center"><Bike className="h-4 w-4 text-white" /></div>
              {company}
            </SheetTitle>
            <p className="text-[10px] text-slate-500 text-left">{user?.name} · {user?.role}</p>
          </SheetHeader>
          <nav className="p-3 space-y-1">
            {navItems.map(([v, label, Icon]) => (
              <button key={v} data-testid={`menu-${v}`} onClick={() => go(v)}
                className={`w-full flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                  tab === v ? "bg-blue-600 text-white" : "text-slate-400 hover:text-white hover:bg-slate-800"}`}>
                <Icon className="h-4 w-4" />{label}
              </button>
            ))}
            {(user?.role === "admin" || user?.screens?.includes("tasks")) && (
              <button data-testid="menu-tasks" onClick={() => { setMenuOpen(false); window.dispatchEvent(new Event("open-tasks")); }}
                className="w-full flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors text-slate-400 hover:text-white hover:bg-slate-800">
                <ClipboardList className="h-4 w-4" />Tasks
              </button>
            )}
            {user?.role === "admin" && (
              <>
                <div className="border-t border-slate-800 my-2" />
                <button data-testid="menu-settings" onClick={() => go("settings")}
                  className={`w-full flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                    tab === "settings" ? "bg-blue-600 text-white" : "text-slate-400 hover:text-white hover:bg-slate-800"}`}>
                  <SettingsIcon className="h-4 w-4" />Settings
                </button>
              </>
            )}
          </nav>
        </SheetContent>
      </Sheet>
    </header>
  );
};
